// eslint-disable-next-line jira/restricted/react-component-props
import React, { Component, type ComponentProps } from 'react';
import type { Store } from 'redux';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import AppStyle from '@atlassian/jira-common-components-app-style/src/index.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { FlagsDispatcher } from '@atlassian/jira-flags';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import flagsMapper from './flags/index.tsx';
import { initialQuery } from './model/constants.tsx';
import type { Props } from './model/index.tsx';
import { isQueryChanged } from './model/query.tsx';
import rootEpic from './ops/index.tsx';
import { setAppProps } from './state/actions/app/index.tsx';
import {
	retrieve as retrieveFilters,
	retrieved as retrievedFilters,
} from './state/actions/filters/index.tsx';
import { setQuery } from './state/actions/query/index.tsx';
import createStore from './state/index.tsx';
import { getQuery } from './state/selectors/query/index.tsx';
import type { State } from './state/types.tsx';
import View from './view/index.tsx';
import { getHeaderConfiguration } from './view/selectors.tsx';

// eslint-disable-next-line jira/react/no-class-components
class Filters extends Component<Props> {
	static defaultProps = {
		query: undefined,
		prefetchedData: {},
		withoutHeader: false,
		withPageReadyMetrics: true,
		onQueryChanged: noop,
		onUnmount: noop,
		isAdmin: false,
	};

	constructor(props: Props) {
		super(props);
		this.store = createStore(rootEpic);
		const { filters } = props.prefetchedData;
		const { dispatch } = this.store;

		dispatch(setAppProps(props));
		dispatch(setQuery(props.query || initialQuery));
		dispatch(filters ? retrievedFilters(filters) : retrieveFilters());
	}

	componentDidUpdate(prevProps: Props): void {
		const { dispatch, getState } = this.store;
		const { query } = this.props;

		if (!isEqual(prevProps, this.props)) {
			dispatch(setAppProps(this.props));
		}

		/**
		 * We compare this.props.query against what's being stored inside the store ONLY IF the query prop is specified
		 * i.e. NOT undefined. When query is passed in as a prop, the expectation is onQueryChanged is also passed in as
		 * a prop. Using these two props, the parent component has the ability to control this component's "current query".
		 * Note that this component is capable to manage its own query independently, which is why the two aforementioned
		 * props are actually optional.
		 */
		// @ts-expect-error - TS2345 - Argument of type 'Partial<Query>' is not assignable to parameter of type 'Query'.
		if (query && isQueryChanged(query, getQuery(getState()))) {
			dispatch(setQuery(query));
			dispatch(retrieveFilters());
		}
	}

	componentWillUnmount(): void {
		this.props.onUnmount();
	}

	store: Store<State>;

	render(): React.JSX.Element {
		const {
			intl: { formatMessage },
		} = this.props;

		const headerConfiguration = getHeaderConfiguration();

		return (
			<AppBase store={this.store} id="filter-directory-main" hasErrorBoundary={false}>
				<ReportErrors id="unhandled" packageName="jiraFiltersMain" teamName="keystone">
					<DocumentTitle
						title={
							// @ts-expect-error - TS2339 - Property 'caption' does not exist on type 'HeaderConfiguration<string>'.
							headerConfiguration.caption
								? // @ts-expect-error - TS2339 - Property 'caption' does not exist on type 'HeaderConfiguration<string>'.
									formatMessage(headerConfiguration.caption)
								: ''
						}
					/>
					<FlagsDispatcher mapper={flagsMapper} />
					<ViewTracker />
					<AppStyle>
						{/* @ts-expect-error - TS2786 - Type 'any' is not assignable to type 'never'. */}
						<View />
					</AppStyle>
				</ReportErrors>
			</AppBase>
		);
	}
}

type FiltersProps = JSX.LibraryManagedAttributes<typeof Filters, ComponentProps<typeof Filters>>;

const FiltersWithContext = (props: Omit<FiltersProps, 'intl'>): React.JSX.Element => {
	const isAdmin = useIsAdmin();
	const intl = useIntl();
	return <Filters {...props} isAdmin={isAdmin} intl={intl} />;
};

export default FiltersWithContext;
